'use client';

import { type MouseEvent, type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import Button from '../button';
import getCtaStyles from '../button/get-cta-styles';
import Image from '../image';
import BevSafeText from '../bev-safe-text';
import { ImagePrimitive } from '@fshr/shared/types';

export type ConversationCardCta = {
	label: string;
	href?: string;
	newWindow?: boolean;
	onClick?: (event: MouseEvent<HTMLElement>) => void;
	intent?: 'filled' | 'outline';
};

export type ConversationCardInfoBlock = {
	heading: string;
	text: string;
	cta?: ConversationCardCta;
};

export type Props = {
	image: ImagePrimitive;
	heading: string;
	infoBlock?: ConversationCardInfoBlock;
	primaryCta: ConversationCardCta;
	secondaryCta?: ConversationCardCta;
	tertiaryCta?: ConversationCardCta;
	className?: string;
};

const TERTIARY_CLASS =
	'text-button2 text-button-primary underline underline-offset-[60%] decoration-solid [text-decoration-skip-ink:none] [text-decoration-thickness:0.1em]';

const INFO_BLOCK_CTA_CLASS =
	'text-button2 text-text-secondary underline decoration-solid [text-decoration-skip-ink:none] [text-decoration-thickness:0.1em]';

function ctaLinkProps(cta: ConversationCardCta) {
	return {
		href: cta.href,
		target: cta.newWindow ? '_blank' : undefined,
		rel: cta.newWindow ? 'noopener' : undefined,
		onClick: cta.onClick,
	};
}

export default function ConversationCard({
	image,
	heading,
	infoBlock,
	primaryCta,
	secondaryCta,
	tertiaryCta,
	className = '',
}: Props): ReactElement {
	const primaryCtaIntent = primaryCta.intent ?? 'filled';

	return (
		<aside
			className={cn(
				'flex w-full max-w-[400px] flex-col items-center gap-y-stack-m bg-surface-default px-card-padding-xs py-card-padding-s shadow-[0_5px_15px_0_rgba(3,3,3,0.12)]',
				className
			)}
		>
			<div className="hidden h-[120px] w-[120px] md:block">
				<Image
					imagePrimitive={image}
					roughSize="s"
					className="block h-full w-full rounded-full object-cover"
				/>
			</div>

			<p className="text-contextual line-clamp-2 text-center text-text-heading">
				<BevSafeText>{heading}</BevSafeText>
			</p>

			{infoBlock && (
				<div className="flex w-full items-center gap-150 border-y border-border-subtle py-100">
					<div className="flex flex-1 flex-col items-start gap-060">
						<span className="text-overline text-text-secondary">
							<BevSafeText>{infoBlock.heading}</BevSafeText>
						</span>
						<span className="text-subtitle2 text-text-secondary">
							<BevSafeText>{infoBlock.text}</BevSafeText>
						</span>
					</div>
					{infoBlock.cta &&
						(infoBlock.cta.href ? (
							<a {...ctaLinkProps(infoBlock.cta)} className={INFO_BLOCK_CTA_CLASS}>
								<BevSafeText>{infoBlock.cta.label}</BevSafeText>
							</a>
						) : (
							<button
								type="button"
								onClick={infoBlock.cta.onClick}
								className={INFO_BLOCK_CTA_CLASS}
							>
								<BevSafeText>{infoBlock.cta.label}</BevSafeText>
							</button>
						))}
				</div>
			)}

			<div className="flex w-full flex-col gap-y-button-stack">
				{primaryCta.href ? (
					<a
						{...ctaLinkProps(primaryCta)}
						className={getCtaStyles({
							intent: primaryCtaIntent,
							size: 'small',
							className: 'w-full',
						})}
					>
						<BevSafeText>{primaryCta.label}</BevSafeText>
					</a>
				) : (
					<Button
						intent={primaryCtaIntent}
						size="small"
						className="w-full"
						onClick={primaryCta.onClick}
					>
						<BevSafeText>{primaryCta.label}</BevSafeText>
					</Button>
				)}

				{secondaryCta &&
					(secondaryCta.href ? (
						<a
							{...ctaLinkProps(secondaryCta)}
							className={getCtaStyles({
								intent: 'outline',
								size: 'small',
								className: 'w-full',
							})}
						>
							<BevSafeText>{secondaryCta.label}</BevSafeText>
						</a>
					) : (
						<Button intent="outline" size="small" className="w-full" onClick={secondaryCta.onClick}>
							<BevSafeText>{secondaryCta.label}</BevSafeText>
						</Button>
					))}
			</div>

			{tertiaryCta &&
				(tertiaryCta.href ? (
					<a {...ctaLinkProps(tertiaryCta)} className={TERTIARY_CLASS}>
						<BevSafeText>{tertiaryCta.label}</BevSafeText>
					</a>
				) : (
					<button type="button" onClick={tertiaryCta.onClick} className={TERTIARY_CLASS}>
						<BevSafeText>{tertiaryCta.label}</BevSafeText>
					</button>
				))}
		</aside>
	);
}
